import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring, useTransform } from "framer-motion";
import { Toaster } from "sonner";
import { Play, Pause } from "lucide-react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import About from "@/components/About";
import Music from "@/components/Music";
import LoveInterlude from "@/components/LoveInterlude";
import Cinematic from "@/components/Cinematic";
import Stage from "@/components/Stage";
import DanceInterlude from "@/components/DanceInterlude";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import { LogoDraw } from "@/components/Logo";

const FIREFLIES = [
  { left: "10%", top: "42%", size: 5, delay: "0s", duration: "9s" },
  { left: "22%", top: "58%", size: 4, delay: "2.4s", duration: "11s" },
  { left: "36%", top: "34%", size: 6, delay: "1.2s", duration: "10s" },
  { left: "50%", top: "52%", size: 4, delay: "3.6s", duration: "12s" },
  { left: "63%", top: "38%", size: 5, delay: "0.8s", duration: "9.5s" },
  { left: "76%", top: "56%", size: 4, delay: "2s", duration: "10.5s" },
  { left: "88%", top: "44%", size: 5, delay: "4.4s", duration: "8.5s" },
  { left: "44%", top: "64%", size: 3, delay: "5.2s", duration: "9.8s" },
];

const SunsetOutro = () => {
  const audioRef = useRef(null);
  const sectionRef = useRef(null);
  const signoffRef = useRef(null);
  const horrorRef = useRef(null);
  const [signoffPlaying, setSignoffPlaying] = useState(false);

  const toggleSignoff = () => {
    const a = signoffRef.current;
    if (!a) return;
    if (a.paused) {
      a.currentTime = 0;
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  };

  // The forest turns sinister while the alien sign-off is on air
  useEffect(() => {
    const v = horrorRef.current;
    if (!v) return;
    if (signoffPlaying) {
      v.currentTime = 0;
      v.play().catch(() => {});
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [signoffPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    const section = sectionRef.current;
    if (!audio || !section) return;
    audio.volume = 0.18;
    const inView = () => {
      const r = section.getBoundingClientRect();
      return r.top < window.innerHeight * 0.75 && r.bottom > 0;
    };
    const tryPlay = () => audio.play().catch(() => {});
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tryPlay();
        } else {
          audio.pause();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(section);
    // Browsers block sound until the visitor has tapped/clicked once — start then if visible
    const unlock = () => {
      if (audio.paused && inView()) tryPlay();
    };
    document.addEventListener("pointerdown", unlock);
    document.addEventListener("keydown", unlock);
    return () => {
      obs.disconnect();
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
      audio.pause();
    };
  }, []);

  return (
    <section ref={sectionRef} data-testid="sunset-outro" className="relative bg-ink" aria-label="Sunset through the forest">
      <audio ref={audioRef} data-testid="crickets-audio" loop preload="auto">
        <source src="/assets/crickets-ambience.ogg" type="audio/ogg" />
        <source src="/assets/crickets-ambience.mp3" type="audio/mpeg" />
      </audio>
      <audio
        ref={signoffRef}
        data-testid="signoff-audio"
        preload="auto"
        onPlay={() => setSignoffPlaying(true)}
        onPause={() => setSignoffPlaying(false)}
        onEnded={() => setSignoffPlaying(false)}
      >
        <source src="/assets/signoff-voice.ogg" type="audio/ogg" />
        <source src="/assets/signoff-voice.mp3" type="audio/mpeg" />
      </audio>
      <div className="relative overflow-hidden">
        <img
          src="/assets/sunset-woods.png"
          alt="Golden sunset bleeding through a dark forest"
          className="sunset-drift w-full h-[280px] lg:h-[440px] object-cover analog-grade"
          loading="lazy"
        />
        <video
          ref={horrorRef}
          data-testid="horror-pov-video"
          className={`absolute inset-0 z-[2] w-full h-full object-cover transition-opacity duration-[1400ms] ${
            signoffPlaying ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          muted
          loop
          playsInline
          preload="none"
        >
          <source src="/assets/horror-pov.webm" type="video/webm" />
          <source src="/assets/horror-pov.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-surface via-transparent to-ink" />
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {FIREFLIES.map((f, i) => (
            <span
              key={i}
              data-testid={`firefly-${i}`}
              className="firefly"
              style={{
                left: f.left,
                top: f.top,
                width: f.size,
                height: f.size,
                animationDelay: f.delay,
                animationDuration: f.duration,
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0 z-[6] flex flex-col items-center justify-center gap-6 px-6 pointer-events-none">
          <p
            data-testid="closing-line"
            className="cover-tagline font-serif italic font-light text-2xl lg:text-4xl text-cream/85 text-center leading-relaxed"
          >
            until the next signal — <span className="text-gold">keep tuning in.</span>
          </p>
          <button
            data-testid="signoff-play-button"
            onClick={toggleSignoff}
            className="group pointer-events-auto inline-flex items-center gap-3 border border-gold/50 bg-ink/55 backdrop-blur-sm rounded-full pl-2 pr-6 py-2 hover:border-gold transition-colors duration-300"
          >
            <span className="w-9 h-9 rounded-full border border-gold bg-ink/60 flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
              {signoffPlaying ? (
                <Pause size={13} className="text-gold fill-gold group-hover:text-ink group-hover:fill-ink" />
              ) : (
                <Play size={13} className="text-gold fill-gold ml-0.5 group-hover:text-ink group-hover:fill-ink" />
              )}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold text-left leading-relaxed">
              {signoffPlaying ? "On air — 1920s sign-off" : "Press play — radio sign-off"}
            </span>
          </button>
        </div>
        <p className="absolute bottom-3 right-4 z-[5] font-mono text-[8px] uppercase tracking-[0.18em] text-cream/25">
          Ambience: dusk pond, crickets — Glaneur de sons, CC BY 3.0
        </p>
      </div>
    </section>
  );
};

const Home = ({ scrollTo, intro, progress, darkness }) => {
  const openingRef = useRef(null);
  const farewellRef = useRef(null);

  // Opening score: short low happy harmony when the site opens,
  // ends for good once the first section (hero intro) is scrolled away from
  useEffect(() => {
    const audio = openingRef.current;
    const farewell = farewellRef.current;
    const heroSec = document.querySelector('[data-testid="hero-section"]');
    if (!audio || !farewell || !heroSec) return;
    audio.volume = 0.5;
    farewell.volume = 0.45;
    let finished = false;
    const tryPlay = () => {
      if (!finished) audio.play().catch(() => {});
    };
    tryPlay();
    const unlock = () => tryPlay();
    document.addEventListener("pointerdown", unlock);
    document.addEventListener("keydown", unlock);
    const onScroll = () => {
      if (finished) return;
      if (heroSec.getBoundingClientRect().bottom < 80) {
        finished = true;
        audio.pause();
        // one final soft chord instead of cutting off
        farewell.currentTime = 0;
        farewell.play().catch(() => {});
        window.removeEventListener("scroll", onScroll);
        document.removeEventListener("pointerdown", unlock);
        document.removeEventListener("keydown", unlock);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  return (
  <>
    <audio ref={openingRef} data-testid="opening-score-audio" preload="auto">
      <source src="/assets/opening-harmony.ogg" type="audio/ogg" />
      <source src="/assets/opening-harmony.mp3" type="audio/mpeg" />
    </audio>
    <audio ref={farewellRef} data-testid="farewell-chord-audio" preload="auto">
      <source src="/assets/farewell-chord.ogg" type="audio/ogg" />
      <source src="/assets/farewell-chord.mp3" type="audio/mpeg" />
    </audio>
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] bg-gold z-[65] origin-left"
      style={{ scaleX: progress }}
      data-testid="scroll-progress-bar"
    />
    <motion.div
      data-testid="scroll-darkness-overlay"
      className="fixed inset-0 z-[55] bg-[#070907] pointer-events-none"
      style={{ opacity: darkness }}
      aria-hidden="true"
    />
    <AnimatePresence>
      {intro && (
        <motion.div
          key="intro-curtain"
          data-testid="intro-curtain"
          className="fixed inset-0 z-[80] bg-surface flex flex-col items-center justify-center gap-6"
          exit={{ y: "-100%" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <LogoDraw className="w-20 h-20" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="font-mono text-[10px] uppercase tracking-[0.4em] text-cream/50"
          >
            Tuning the signal
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>

    <Header onNav={scrollTo} />
    <main>
      <Hero onNav={scrollTo} />
      <Marquee />
      <About />
      <Music />
      <LoveInterlude />
      <Cinematic />
      <DanceInterlude />
      <Stage />
      <Contact />
      <SunsetOutro />
    </main>
    <Footer onNav={scrollTo} />
  </>
  );
};

function App() {
  const lenisRef = useRef(null);
  const [intro, setIntro] = useState(true);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });
  const darkness = useTransform(scrollYProgress, [0, 1], [0, 0.5]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.35, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setIntro(false), 1400);
    return () => clearTimeout(t);
  }, []);

  // Media protection: no right-click save / drag-out on images, video or audio
  useEffect(() => {
    const isFormField = (el) => el.closest("input, textarea, select, [contenteditable='true']");
    const isMedia = (el) => el.closest("img, video, audio, picture, svg");
    const blockContext = (e) => {
      if (!isFormField(e.target) && isMedia(e.target)) e.preventDefault();
    };
    const blockDrag = (e) => {
      if (isMedia(e.target)) e.preventDefault();
    };
    document.addEventListener("contextmenu", blockContext);
    document.addEventListener("dragstart", blockDrag);
    return () => {
      document.removeEventListener("contextmenu", blockContext);
      document.removeEventListener("dragstart", blockDrag);
    };
  }, []);

  const scrollTo = (id) => {
    lenisRef.current?.scrollTo(`#${id}`, { offset: id === "top" ? 0 : -60, duration: 1.6 });
  };

  return (
    <div className="bg-ink text-cream min-h-screen" data-testid="app-root">
      <div className="grain-overlay" aria-hidden="true" />
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={<Home scrollTo={scrollTo} intro={intro} progress={progress} darkness={darkness} />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
